import * as yup from 'yup'
import { isPatientRN } from 'presentation/utils/validators/is-patient-rn'
import moment from 'moment-timezone'
import { COOPERATIVE_HOSPITALS } from 'presentation/contexts/surgical-order-v2/initial-state'

const validationSchema = yup.object().shape({
  hospitalId: yup
    .number()
    .positive('Selecione uma unidade')
    .required('Selecione uma unidade'),
  surgeryCenterId: yup
    .number()
    .positive('Selecione uma unidade cirúrgica')
    .required('Selecione uma unidade cirúrgica'),
  cpf: yup.string().when('birthDate', {
    is: (birthDate: Date | undefined | null) => {
      if (!birthDate) return true
      try {
        return !isPatientRN(new Date(birthDate))
      } catch {
        return true
      }
    },
    then: yup.string().cpf('CPF inválido').required('CPF é obrigatório'),
    otherwise: yup
      .string()
      .test('conditional-cpf', 'CPF inválido', function (value) {
        if (!value) return true
        // Se houver valor preenchido, valida se o CPF é válido
        return yup.string().cpf().isValidSync(value)
      })
      .notRequired()
      .nullable()
  }),
  name: yup.string().required(),
  birthDate: yup
    .date()
    .typeError('Data inválida')
    .max(
      new Date(),
      'Data futura não permitida. Escolha uma data igual ou anterior à data atual'
    )
    .required(),
  gender: yup.string().required(),
  maritalStatus: yup.string().when('birthDate', {
    is: (birthDate: Date | undefined | null) => {
      if (!birthDate) return true
      try {
        return !isPatientRN(new Date(birthDate))
      } catch {
        return true
      }
    },
    then: yup.string().required(),
    otherwise: yup.string().notRequired().nullable()
  }),
  phone: yup.string().when('birthDate', {
    is: (birthDate: Date | undefined | null) => {
      if (!birthDate) return true
      try {
        return !isPatientRN(new Date(birthDate))
      } catch {
        return true
      }
    },
    then: yup
      .string()
      .required('O telefone é obrigatório')
      .transform((value) => (value ? value.replace(/\D/g, '') : ''))
      .matches(
        /^\d{2}9\d{8}|55\d{2}9\d{8}$/,
        'O telefone deve estar no formato correto com DDI + DDD + número iniciando com 9'
      ),
    otherwise: yup.string().notRequired().nullable()
  }),
  email: yup.string().when('birthDate', {
    is: (birthDate: Date | undefined | null) => {
      if (!birthDate) return true
      try {
        return !isPatientRN(new Date(birthDate))
      } catch {
        return true
      }
    },
    then: yup.string().email().required(),
    otherwise: yup.string().notRequired().nullable()
  }),
  healthInsuranceCode: yup
    .number()
    .positive('Selecione um convênio')
    .required('Selecione um convênio'),
  healthCard: yup.string().required(),
  cooperative: yup.string().when('hospitalId', {
    is: (hospital: string) => COOPERATIVE_HOSPITALS.includes(Number(hospital)),
    then: yup.string().required()
  }),
  tutor: yup.object().when('birthDate', {
    is: (birthDate: Date) => {
      try {
        return birthDate && isPatientRN(new Date(birthDate))
      } catch {
        return false
      }
    },
    then: yup.object({
      cpf: yup.string().cpf().required('CPF da mãe é obrigatório'),
      name: yup.string().required('Nome da mãe é obrigatório'),
      phone: yup
        .string()
        .required('O telefone da mãe é obrigatório')
        .transform((value) => (value ? value.replace(/\D/g, '') : ''))
        .matches(
          /^\d{2}9\d{8}|55\d{2}9\d{8}$/,
          'O telefone deve estar no formato correto'
        ),
      email: yup
        .string()
        .email('E-mail inválido')
        .required('E-mail da mãe é obrigatório')
    }),
    otherwise: yup.object().nullable()
  }),
  validThru: yup
    .date()
    .typeError('Data Inválida')
    .format('YYYY-MM-DD', true)
    .optional()
    .test(
      'greater_then_today',
      'A data informada não pode ser menor que a atual.',
      (value) => {
        if (value) {
          const date = moment(value, 'YYYY-MM-DD').startOf('day')
          const now = moment().startOf('day')
          if (date.isValid()) {
            return date >= now
          }
        }
        return true
      }
    )
})

export default validationSchema
